import { typedEntries } from '../functional';

/** A primitive value that can be serialised into a query-string parameter. */
export type QueryParamValue = string | number | boolean;

/**
 * A map of query-string parameters. Values may be primitives, arrays of
 * primitives, or `null`/`undefined`. Nullish values (and nullish items inside
 * arrays) are skipped, so callers can pass optional values directly without
 * pre-filtering.
 */
export type QueryParams = Record<
  string,
  | QueryParamValue
  | readonly (QueryParamValue | null | undefined)[]
  | null
  | undefined
>;

/**
 * Appends query parameters to a URL. Array values are appended as repeated
 * keys and every value is URL-encoded. `null`/`undefined` values (and nullish
 * items inside arrays) are skipped. Returns the original URL unchanged when no
 * parameters are provided or the resulting query is empty.
 *
 * Any fragment (`#...`) is split off first so the query is inserted before
 * it, and the `?`/`&` separator is chosen from the base part only. This keeps
 * fragments intact (`/path#hash` -> `/path?x=1#hash`) and avoids doubling up
 * separators when the URL already ends in `?` or `&`.
 *
 * @param url - The base URL (may include a query string and/or fragment).
 * @param params - Optional query parameters to append.
 * @returns The URL with the serialised query string appended.
 *
 * @example
 * buildUrl('/api/users', { page: 2, tag: ['a', 'b'] });
 * // => '/api/users?page=2&tag=a&tag=b'
 *
 * buildUrl('/api/users#section', { page: 2 });
 * // => '/api/users?page=2#section'
 */
export const buildUrl = (url: string, params?: QueryParams): string => {
  if (!params) {
    return url;
  }

  const search = new URLSearchParams();
  typedEntries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item === null || item === undefined) {
        return;
      }
      search.append(key, String(item));
    });
  });

  const query = search.toString();
  if (!query) {
    return url;
  }

  // Split off any fragment so the query is inserted before it.
  const hashIndex = url.indexOf('#');
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

  // Choose the separator based on the base part only.
  let separator = '&';
  if (!base.includes('?')) {
    separator = '?';
  } else if (base.endsWith('?') || base.endsWith('&')) {
    separator = '';
  }

  return `${base}${separator}${query}${hash}`;
};
